<template>
  <article
    class="product-card"
    :class="{ 'is-digital': isDigital, 'is-sold-out': isSoldOut, 'has-added-feedback': addedFeedback }"
    :aria-labelledby="productTitleId"
    :aria-describedby="product.description ? productDescriptionId : null">
    <button type="button" class="card-detail-action" :disabled="isSoldOut" :aria-label="detailAriaLabel" aria-haspopup="dialog" @click="openDetail">
      <span class="sr-only">Produktdetails öffnen</span>
    </button>

    <div class="product-image-wrap">
      <div v-if="imageLoading" class="image-loading" aria-hidden="true"></div>
      <img v-else-if="imageUrl && !imageError" :src="imageUrl" :alt="product.title" class="product-image" @error="handleImageError" />
      <div v-else class="product-image-placeholder" role="img" :aria-label="`Kein Vorschaubild für ${product.title}`">
        <v-icon size="48" color="grey lighten-1">
          {{ isDigital ? "mdi-file-pdf-box" : "mdi-image-off-outline" }}
        </v-icon>
        <span>Keine Vorschau</span>
      </div>

      <div class="badge-rail" aria-hidden="true">
        <div class="badge-stack">
          <div v-if="isDigital" class="type-badge digital">
            <v-icon x-small color="white">mdi-download</v-icon>
            <span>Download</span>
          </div>
          <div v-if="isSoldOut" class="type-badge sold-out">
            <v-icon x-small color="white">mdi-alert-circle</v-icon>
            <span>Ausverkauft</span>
          </div>
        </div>
      </div>

      <div v-if="cartQuantity > 0" class="cart-badge" aria-hidden="true">
        <v-icon x-small color="white">mdi-cart-check</v-icon>
        <span>{{ cartQuantity }}</span>
      </div>

      <div v-if="product.images && product.images.length > 1" class="image-count" aria-hidden="true">
        <v-icon x-small color="white">mdi-image-multiple</v-icon>
        <span>{{ product.images.length }}</span>
      </div>
    </div>

    <div class="product-body">
      <h3 :id="productTitleId" class="product-title">{{ product.title }}</h3>
      <div v-if="product.description" :id="productDescriptionId" class="product-description">{{ product.description }}</div>

      <div class="product-meta">
        <span class="product-format">
          <v-icon x-small color="grey lighten-1">{{ isDigital ? "mdi-file-download-outline" : "mdi-package-variant-closed" }}</v-icon>
          {{ isDigital ? "PDF-Download" : `${minimumQuantity} – ${product.maxQuantity || 100} Stk` }}
        </span>
        <span v-if="isLowStock" class="low-stock" role="status">
          <v-icon x-small color="orange lighten-1">mdi-alert-outline</v-icon>
          Nur noch {{ product.stock }} verfügbar
        </span>
      </div>

      <div class="product-actions" @click.stop @keydown.stop>
        <div v-if="!isDigital && !hasOptions" class="quantity-row">
          <span class="quantity-label">Menge</span>
          <div class="qty-stepper" aria-label="Bestellmenge">
            <v-btn
              color="white"
              icon
              class="qty-control"
              :aria-label="`Menge für ${product.title} verringern`"
              @click="decreaseQty"
              :disabled="qty <= minimumQuantity || isSoldOut">
              <v-icon small>mdi-minus</v-icon>
            </v-btn>
            <input
              v-model.number="qty"
              type="number"
              class="qty-input"
              :aria-label="`Menge für ${product.title}`"
              :min="minimumQuantity"
              :max="maxAllowedQty"
              :disabled="isSoldOut"
              @blur="clampQty" />
            <v-btn
              color="white"
              icon
              class="qty-control"
              :aria-label="`Menge für ${product.title} erhöhen`"
              @click="increaseQty"
              :disabled="qty >= maxAllowedQty || isSoldOut">
              <v-icon small>mdi-plus</v-icon>
            </v-btn>
          </div>
        </div>

        <v-btn
          color="primary"
          depressed
          rounded
          class="add-btn"
          :class="{ 'is-added': addedFeedback }"
          :aria-label="ctaAriaLabel"
          @click="addToCart"
          :disabled="isSoldOut">
          <v-icon left small class="cta-icon">{{ ctaIcon }}</v-icon>
          {{ ctaLabel }}
        </v-btn>

        <span class="sr-only" aria-live="polite">{{ addedFeedback ? `${product.title} wurde zum Warenkorb hinzugefügt.` : "" }}</span>
      </div>
    </div>
  </article>
</template>

<script>
import api from "@/api"

export default {
  name: "ProductCard",
  props: {
    product: { type: Object, required: true },
    cartQuantity: { type: Number, default: 0 },
  },
  data() {
    return {
      qty: this.product.minQuantity || 1,
      imageUrl: null,
      imageError: false,
      imageLoading: false,
      imageRequestId: 0,
      addedFeedback: false,
      feedbackTimer: null,
    }
  },
  computed: {
    productTitleId() {
      return `product-title-${this.product._id || this._uid}`
    },
    productDescriptionId() {
      return `product-description-${this.product._id || this._uid}`
    },
    imageSignature() {
      const firstImage = this.product.images && this.product.images[0]
      return `${this.product._id || ""}:${firstImage ? firstImage.fileId : ""}`
    },
    minimumQuantity() {
      return this.product.minQuantity || 1
    },
    hasOptions() {
      return !!(this.product.options && this.product.options.length > 0)
    },
    isDigital() {
      return this.product.productType === "digital"
    },
    isSoldOut() {
      if (!this.product) return false
      if (this.hasOptions) {
        if (!this.product.variantStock || Object.keys(this.product.variantStock).length === 0) {
          return false
        }
        const combinations = this.getProductCombinations(this.product)
        if (combinations.length === 0) return false

        let hasAvailable = false
        for (const sig of combinations) {
          const val = this.product.variantStock[sig]
          if (val === undefined || val === null || val >= this.minimumQuantity) {
            hasAvailable = true
            break
          }
        }
        return !hasAvailable
      }
      return this.product.stock !== undefined && this.product.stock !== null && this.product.stock < this.minimumQuantity
    },
    isLowStock() {
      if (this.hasOptions || this.product.stock === undefined || this.product.stock === null) return false
      const stock = Number(this.product.stock)
      const threshold = Math.max(10, this.minimumQuantity)
      return stock >= this.minimumQuantity && stock <= threshold
    },
    maxAllowedQty() {
      if (!this.product) return 100
      if (this.hasOptions) {
        return this.product.maxQuantity || 100
      }
      if (this.product.stock !== undefined && this.product.stock !== null) {
        return Math.max(this.minimumQuantity, Math.min(this.product.maxQuantity || 100, this.product.stock))
      }
      return this.product.maxQuantity || 100
    },
    detailAriaLabel() {
      const cartInfo = this.cartQuantity > 0 ? `, ${this.cartQuantity} bereits im Warenkorb` : ""
      return `${this.product.title}${cartInfo}. Produktdetails öffnen.`
    },
    ctaLabel() {
      if (this.isSoldOut) return "Ausverkauft"
      if (this.addedFeedback) return "Hinzugefügt"
      if (this.hasOptions) return "Variante wählen"
      return "In den Warenkorb"
    },
    ctaIcon() {
      if (this.isSoldOut) return "mdi-close-circle-outline"
      if (this.addedFeedback) return "mdi-check"
      if (this.hasOptions) return "mdi-tune-variant"
      return "mdi-cart-plus"
    },
    ctaAriaLabel() {
      if (this.hasOptions && !this.isSoldOut) return `Variante für ${this.product.title} wählen`
      if (this.isSoldOut) return `${this.product.title} ist ausverkauft`
      return `${this.qty} Stück ${this.product.title} in den Warenkorb`
    },
  },
  watch: {
    "product._id"() {
      this.qty = this.minimumQuantity
      this.addedFeedback = false
    },
    imageSignature: {
      immediate: true,
      handler() {
        this.loadImage()
      },
    },
  },
  methods: {
    openDetail() {
      if (!this.isSoldOut) this.$emit("open-detail", this.product)
    },
    async loadImage() {
      if (this.imageUrl) {
        URL.revokeObjectURL(this.imageUrl)
        this.imageUrl = null
      }
      const requestId = ++this.imageRequestId
      this.imageError = false
      const firstImage = this.product.images && this.product.images[0]
      if (!firstImage) {
        this.imageLoading = false
        return
      }
      this.imageLoading = true
      try {
        const url = await api.marketingShop.loadProductImage(this.product._id, firstImage.fileId)
        if (requestId !== this.imageRequestId) {
          URL.revokeObjectURL(url)
          return
        }
        this.imageUrl = url
      } catch (e) {
        if (requestId === this.imageRequestId) {
          console.error("Bild konnte nicht geladen werden:", e)
          this.imageError = true
        }
      } finally {
        if (requestId === this.imageRequestId) this.imageLoading = false
      }
    },
    handleImageError() {
      this.imageError = true
    },
    increaseQty() {
      this.qty = Math.min((this.qty || 1) + 1, this.maxAllowedQty)
    },
    decreaseQty() {
      this.qty = Math.max((this.qty || 1) - 1, this.minimumQuantity)
    },
    clampQty() {
      let v = parseInt(this.qty, 10) || this.minimumQuantity
      v = Math.max(this.minimumQuantity, Math.min(this.maxAllowedQty, v))
      this.qty = v
    },
    addToCart() {
      if (this.hasOptions) {
        this.openDetail()
        return
      }
      if (this.isDigital) {
        this.$emit("add-to-cart", { product: this.product, quantity: 1 })
        this.showAddedFeedback()
        return
      }
      this.clampQty()
      this.$emit("add-to-cart", { product: this.product, quantity: this.qty })
      this.showAddedFeedback()
      this.qty = this.minimumQuantity
    },
    showAddedFeedback() {
      if (this.feedbackTimer) clearTimeout(this.feedbackTimer)
      this.addedFeedback = true
      this.feedbackTimer = setTimeout(() => {
        this.addedFeedback = false
        this.feedbackTimer = null
      }, 1200)
    },
    getProductCombinations(product) {
      if (!product || !product.options || product.options.length === 0) return []
      const validOptions = product.options.filter((o) => o.name && o.name.trim() && o.choices && o.choices.some((c) => c.value && c.value.trim()))
      if (validOptions.length === 0) return []

      let results = [[]]
      for (const option of validOptions) {
        const nextResults = []
        const choices = option.choices.filter((c) => c.value && c.value.trim())
        for (const res of results) {
          for (const choice of choices) {
            nextResults.push([...res, choice.id])
          }
        }
        results = nextResults
      }

      return results.map((comb) => comb.sort().join(","))
    },
  },
  beforeDestroy() {
    this.imageRequestId += 1
    if (this.imageUrl) URL.revokeObjectURL(this.imageUrl)
    if (this.feedbackTimer) clearTimeout(this.feedbackTimer)
  },
}
</script>

<style scoped>
.product-card {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.065), rgba(255, 255, 255, 0.035));
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transition: transform 220ms ease, border-color 220ms ease, background-color 220ms ease, box-shadow 220ms ease;
}

.product-card:not(.is-sold-out):hover {
  border-color: rgba(74, 222, 128, 0.55);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.085), rgba(255, 255, 255, 0.045));
  transform: translateY(-3px);
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.24);
}

.product-card.is-digital {
  border-color: rgba(74, 222, 128, 0.25);
}

.product-card.is-sold-out {
  cursor: default;
  background: rgba(255, 255, 255, 0.025);
}

.card-detail-action {
  position: absolute;
  inset: 0;
  z-index: 3;
  width: 100%;
  border: 0;
  border-radius: inherit;
  padding: 0;
  background: transparent;
  cursor: inherit;
}

.card-detail-action:focus-visible {
  outline: 3px solid #4ade80;
  outline-offset: -3px;
}

.card-detail-action:disabled {
  cursor: default;
}

.product-image-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #f4f6f5;
  overflow: hidden;
}

.product-image {
  width: 100%;
  height: 100%;
  display: block;
  background-color: transparent;
  object-fit: contain;
  transition: transform 300ms ease, opacity 180ms ease;
}

.product-card:not(.is-sold-out):hover .product-image {
  transform: scale(1.05);
}

.product-card.is-sold-out .product-image {
  filter: grayscale(0.65);
  opacity: 0.58;
}

.image-loading {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: #e8ece9;
}

.image-loading::after {
  position: absolute;
  inset: 0;
  content: "";
  background: linear-gradient(100deg, transparent 20%, rgba(255, 255, 255, 0.72) 48%, transparent 76%);
  transform: translateX(-100%);
  animation: image-shimmer 1.25s ease-in-out infinite;
}

@keyframes image-shimmer {
  to {
    transform: translateX(100%);
  }
}

.product-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  color: #68736c;
  background: linear-gradient(145deg, #f5f7f6, #e8edea);
  font-size: 12px;
  font-weight: 600;
}

.badge-rail {
  position: absolute;
  z-index: 4;
  top: 10px;
  left: 10px;
  display: flex;
  align-items: flex-start;
  pointer-events: none;
}

.badge-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
}

.type-badge,
.cart-badge,
.image-count {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: white;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  padding: 6px 9px;
  border-radius: 999px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.24);
  backdrop-filter: blur(8px);
}

.type-badge.digital {
  background: rgba(0, 117, 50, 0.94);
}

.type-badge.sold-out {
  background: rgba(181, 42, 42, 0.95);
}

.cart-badge {
  position: absolute;
  z-index: 4;
  bottom: 10px;
  left: 10px;
  flex-shrink: 0;
  background: rgba(17, 24, 20, 0.88);
  pointer-events: none;
}

.image-count {
  position: absolute;
  z-index: 4;
  right: 10px;
  bottom: 10px;
  padding: 5px 8px;
  background: rgba(17, 24, 20, 0.72);
  pointer-events: none;
}

.product-body {
  position: relative;
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.product-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 6px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-description {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.66);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 12px;
}

.product-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-top: auto;
  margin-bottom: 14px;
}

.product-format,
.low-stock {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
}

.product-format {
  color: rgba(255, 255, 255, 0.64);
}

.low-stock {
  color: #ffbd6a;
}

.product-actions {
  position: relative;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;
  gap: 10px;
}

.quantity-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  min-height: 46px;
}

.quantity-label {
  flex: 0 0 auto;
  color: rgba(255, 255, 255, 0.66);
  font-size: 12px;
  font-weight: 650;
}

.qty-stepper {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  max-width: 100%;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  padding: 2px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.qty-control {
  width: 40px !important;
  height: 40px !important;
}

.qty-input {
  width: 38px;
  height: 40px;
  text-align: center;
  background: transparent;
  border: none;
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  outline: none;
  -moz-appearance: textfield;
}

.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.add-btn {
  flex: 0 0 auto;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  min-height: 44px;
  text-transform: none !important;
  font-weight: 700;
  letter-spacing: 0 !important;
  transition: background-color 180ms ease, color 180ms ease, transform 180ms ease;
}

.add-btn.is-added {
  background-color: #228b4e !important;
}

.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

@media (max-width: 600px) {
  .product-body {
    padding: 12px;
  }

  .product-title {
    font-size: 14px;
  }

  .product-description {
    font-size: 12px;
    -webkit-line-clamp: 1;
  }

  .quantity-row {
    display: block;
    min-height: 46px;
  }

  .quantity-label {
    display: none;
  }

  .qty-stepper {
    width: 100%;
    justify-content: space-between;
  }

  .add-btn {
    padding-right: 10px !important;
    padding-left: 10px !important;
    font-size: 12px;
  }

  .cta-icon {
    display: none !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .product-card,
  .product-image,
  .add-btn {
    transition: none;
  }

  .product-card:not(.is-sold-out):hover,
  .product-card:not(.is-sold-out):hover .product-image {
    transform: none;
  }

  .image-loading::after {
    animation: none;
  }
}
</style>
